import { skipToken } from "@reduxjs/toolkit/query";

import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";
import { useGetManagedEmployerQuery } from "@js/apps/employer/hooks";
import { useFetchInitialOfferQueryState } from "@js/apps/jobs/apps/bids/api/api";
import { useIdParam } from "@js/hooks/use-id-param";
import type { EmployerOffer, EmployerOfferBid } from "@js/types/jobs";

import { useGetEmployerOfferQueryState } from "../../api";

type UseCrateOrEditOfferMetaReturnPayload = {
  employer_offer_id: number | undefined;
  bid: EmployerOfferBid | (number & EmployerOfferBid);
  job_id: number;
  job_title: string;
  job_type: EnumType<typeof ENUMS.JobType>;
  jobRoleName: string;
  status: EnumType<typeof ENUMS.OfferStatus> | null;
  talent_name: string;
  isDepositRequired: boolean;
  shouldShowContractTermsTypeField: boolean;
} | null;

export const useCreateOrEditOfferMeta =
  (): UseCrateOrEditOfferMetaReturnPayload => {
    const bidId = useIdParam("bidId");
    const offerId = useIdParam("offerId");
    const isNodeOrImpersonated = useIsNodeOrImpersonated();

    // bidId is for new offers, offerId for edit
    const { data: employerOffer } = useGetEmployerOfferQueryState(
      offerId ? offerId : skipToken,
    );
    const { data: initialOffer } = useFetchInitialOfferQueryState(
      bidId ? { bidId } : skipToken,
    );

    const { data: employerProfile } = useGetManagedEmployerQuery();
    const isDepositRequired = employerProfile?.offer_deposit_required;

    const offer = initialOffer || employerOffer;
    if (!offer) {
      return null;
    }
    const isFreelanceJob = offer.bid.job.job_type === ENUMS.JobType.FREELANCE;
    const isMSAClientOrOfferHasContractTerms =
      !!employerOffer?.contract_terms ||
      initialOffer?.msa_client_type === ENUMS.MSAClientType.MSA_CLIENT;
    const shouldShowContractTermsTypeField =
      isNodeOrImpersonated &&
      isFreelanceJob &&
      isMSAClientOrOfferHasContractTerms;

    return {
      employer_offer_id:
        offer === employerOffer ? (offer as EmployerOffer).id : undefined,
      bid: offer.bid,
      job_id: offer.bid.job.id,
      job_title: offer.bid.job.title,
      job_type: offer.bid.job.job_type,
      jobRoleName: offer.bid.job.role.name,
      status: offer.status || null, // null is required
      talent_name: offer.bid.freelancer.user.first_name,
      isDepositRequired: !!isDepositRequired,
      shouldShowContractTermsTypeField,
    };
  };
